import React, { lazy, Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import Layout from '@/components/layout/Layout';
import { PageHeaderProvider } from '@/context/PageHeaderContext';
import { createPageUrl } from '@/utils/routing';
import { AuthProvider } from '@/hooks/useAuth';
import ScrollToTop from '@/components/shared/ScrollToTop';
import { RootErrorBoundary } from '@/components/shared/RootErrorBoundary';
import { ThemeInit } from '@/components/shared/ThemeInit';
import { ToastClickToDismiss } from '@/components/shared/ToastClickToDismiss';
import { PushForegroundBridge } from '@/platform/PushForegroundBridge';
import { getPlatform } from '@/utils/mobile';
import { registerAppQueryClient } from '@/utils/queryClientSingleton';
import './index.css';

// === Logger Setup ===

let _loggerPromise = null;

function getLoggerModule() {
  if (!_loggerPromise) {
    _loggerPromise = import('@/utils/logger').then(module => module).catch(() => null);
  }
  return _loggerPromise;
}

function createLoggerMethod(method) {
  return function(...args) {
    getLoggerModule().then(module => {
      if (module?.mobileLogger?.[method]) {
        module.mobileLogger[method](...args);
      }
    }).catch(() => {});
  };
}

const mobileLoggerSafe = {
  info: createLoggerMethod('info'),
  warn: createLoggerMethod('warn'),
  error: createLoggerMethod('error'),
  log: createLoggerMethod('log'),
  debug: createLoggerMethod('debug')
};

const Home = lazy(() => import('@/pages/Home'));
const Compendium = lazy(() => import('@/pages/Compendium'));
const Rules = lazy(() => import('@/pages/Rules'));
const CharacterCreator = lazy(() => import('@/pages/CharacterCreator'));
const CharacterCreatorRandomize = lazy(() => import('@/pages/CharacterCreatorRandomize'));
const CharacterSheet = lazy(() => import('@/pages/CharacterSheet'));
const LevelUp = lazy(() => import('@/pages/LevelUp'));
const MyCharacters = lazy(() => import('@/pages/MyCharacters'));
const CombatTracker = lazy(() => import('@/pages/CombatTracker'));
const EncounterBuilder = lazy(() => import('@/pages/EncounterBuilder'));
const DMScreen = lazy(() => import('@/pages/DMScreen'));
const Campaigns = lazy(() => import('@/pages/Campaigns'));
const CampaignDetail = lazy(() => import('@/pages/CampaignDetail'));
const Wiki = lazy(() => import('@/pages/Wiki'));
const Generators = lazy(() => import('@/pages/Generators'));
const Homebrew = lazy(() => import('@/pages/Homebrew'));
const SpellBook = lazy(() => import('@/pages/SpellBook'));
const NewPlayer = lazy(() => import('@/pages/NewPlayer'));
const NewDM = lazy(() => import('@/pages/NewDM'));
const Glossary = lazy(() => import('@/components/rules/Glossary'));
const Macros = lazy(() => import('@/pages/Macros'));
const Login = lazy(() => import('@/pages/Login'));
const Upgrade = lazy(() => import('@/pages/Upgrade'));
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));
const CampaignJoin = lazy(() => import('@/pages/CampaignJoin'));
const More = lazy(() => import('@/pages/More'));
const Legal = lazy(() => import('@/pages/Legal'));
const Privacy = lazy(() => import('@/pages/Privacy'));
const Terms = lazy(() => import('@/pages/Terms'));
const Account = lazy(() => import('@/pages/Account'));
const BugReport = lazy(() => import('@/pages/BugReport'));
const NotFound = lazy(() => import('@/pages/NotFound'));
const DesignSystem = lazy(() => import('@/pages/DesignSystem'));

function LoadingFallback() {
  return (
    <div className="flex items-center justify-center h-screen bg-bg-page">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-forest-green mx-auto mb-4"></div>
        <p className="text-forest-green">Loading...</p>
      </div>
    </div>
  );
}

let loadingFallbackTimeoutId = null;

function markMythkitAppMounted() {
  if (typeof window !== 'undefined') {
    window.__mythkitAppMounted = true;
  }
}

function HideLoadingFallback() {
  React.useEffect(() => {
    markMythkitAppMounted();

    if (loadingFallbackTimeoutId !== null) {
      clearTimeout(loadingFallbackTimeoutId);
      loadingFallbackTimeoutId = null;
    }
    
    if (typeof window !== 'undefined' && window.__htmlLoadingTimeoutId) {
      clearTimeout(window.__htmlLoadingTimeoutId);
      window.__htmlLoadingTimeoutId = null;
    }
    
    const fallback = document.getElementById('loading-fallback');
    if (fallback) {
      setTimeout(() => {
        fallback.style.display = 'none';
      }, 100);
    }
  }, []);
  return null;
}

function setupLoadingFallback() {
  const MAX_LOAD_TIME = 10000;
  const fallback = document.getElementById('loading-fallback');
  
  if (fallback) {
    loadingFallbackTimeoutId = setTimeout(() => {
      const root = document.getElementById('root');
      const hasReactContent =
        typeof window !== 'undefined' && window.__mythkitAppMounted === true;
      
      const stillVisible = fallback.style.display !== 'none';
      if (stillVisible && !hasReactContent) {
        fallback.style.display = 'none';
        
        root.innerHTML = `
          <div style="display: flex; align-items: center; justify-content: center; height: 100vh; padding: 20px; text-align: center; background: var(--parchment, #FFF8E7); color: var(--text-dark, #2C1810);">
            <div>
              <h1 style="margin-bottom: 16px; color: var(--forest-green, #2D5016);">App Loading Issue</h1>
              <p style="margin-bottom: 16px;">The app took too long to load. Please check your connection and try again.</p>
              <button onclick="window.location.reload()" style="padding: 12px 24px; background: var(--forest-green, #2D5016); color: white; border: none; border-radius: 8px; cursor: pointer; font-weight: bold;">
                Reload App
              </button>
            </div>
          </div>
        `;
      } else if (stillVisible && hasReactContent) {
        fallback.style.display = 'none';
      }
      
      loadingFallbackTimeoutId = null;
    }, MAX_LOAD_TIME);
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 2 * 60 * 1000,
    },
  },
});
registerAppQueryClient(queryClient);

window.addEventListener('error', (event) => {
  try {
    mobileLoggerSafe.error('Global error:', event.error?.message || event.message, {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      error: event.error
    });
  } catch {
  }
});

window.addEventListener('unhandledrejection', (event) => {
  try {
    mobileLoggerSafe.error('Unhandled promise rejection:', event.reason);
  } catch {
  }
});

let root = null;

// Reuse existing root when container was already passed to createRoot (e.g. HMR or double init)
function getOrCreateRoot() {
  const el = document.getElementById('root');
  if (!el) return null;
  if (el._reactRoot != null) return el._reactRoot;
  const r = ReactDOM.createRoot(el);
  el._reactRoot = r;
  return r;
}

const rootEl = document.getElementById('root');
if (!rootEl) {
  const errorMsg = 'Root element (#root) not found. This may indicate a build or deployment issue.';
  try {
    mobileLoggerSafe.error('=== CRITICAL ERROR ===', errorMsg, {
      readyState: document.readyState,
      hasBody: !!document.body,
      hasDocumentElement: !!document.documentElement,
    });
  } catch {
  }

  if (document.body) {
    try {
      document.body.innerHTML = `
        <div style="display: flex; align-items: center; justify-content: center; height: 100vh; padding: 20px; text-align: center; background: #FFF8E7; color: #2C1810;">
          <div>
            <h1 style="margin-bottom: 16px; color: #2D5016;">Initialization Error</h1>
            <p style="margin-bottom: 16px;">${errorMsg}</p>
            <p style="margin-bottom: 16px; font-size: 0.9em; color: #6B5D54;">If this persists, try reloading the app.</p>
            <button onclick="window.location.reload()" style="padding: 12px 24px; background: #2D5016; color: white; border: none; border-radius: 8px; cursor: pointer; font-weight: bold;">
              Reload App
            </button>
          </div>
        </div>
      `;
    } catch {
    }
  }

  throw new Error(errorMsg);
}

async function initApp() {
  // --- One-time: clear stale service worker on Android so we load fresh index.html from the bundle ---
  try {
    const platform = await getPlatform();
    if (platform === 'android' && typeof navigator !== 'undefined' && navigator.serviceWorker) {
      const doneKey = 'mythkit_sw_clean_done';
      if (!localStorage.getItem(doneKey)) {
        const regs = await navigator.serviceWorker.getRegistrations();
        for (const reg of regs) await reg.unregister();
        localStorage.setItem(doneKey, '1');
        mobileLoggerSafe.info('[Init] Unregistered service worker so app can load fresh bundle');
        window.location.reload();
        return;
      }
    }
  } catch (e) {
    // ignore
  }

  try {
    if (typeof window !== 'undefined' && window.Capacitor) {
      const Capacitor = window.Capacitor;
      const platform = Capacitor.getPlatform();
      const isNative = Capacitor.isNativePlatform();
      mobileLoggerSafe.info('[Init] Capacitor platform:', platform);
      mobileLoggerSafe.info('[Init] Capacitor isNativePlatform:', isNative);
      
      if (Capacitor.Plugins?.App) {
        try {
          await Promise.race([
            Capacitor.Plugins.App.addListener('appStateChange', ({ isActive }) => {
              mobileLoggerSafe.info('App state changed. Is active?', isActive);
            }),
            new Promise((_, reject) => 
              setTimeout(() => reject(new Error('Capacitor listener timeout')), 2000)
            )
          ]);
          mobileLoggerSafe.info('[Init] Capacitor listener setup complete');
        } catch (listenerError) {
          mobileLoggerSafe.warn('[Init] Capacitor listener setup failed or timed out (continuing anyway):', listenerError);
        }
      }
    } else {
      mobileLoggerSafe.info('[Init] Capacitor not available (running in browser)');
    }
  } catch (e) {
    mobileLoggerSafe.warn('[Init] Capacitor check error (continuing anyway):', e);
  }

  mobileLoggerSafe.info('[Init] Creating React root...');
  root = getOrCreateRoot();
  if (!root) {
    throw new Error('Root element (#root) not available for React root');
  }

  mobileLoggerSafe.info('[Init] Rendering React app...');
  
  try {
    root.render(
    <React.StrictMode>
    <RootErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ThemeInit>
          <BrowserRouter
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            }}
          >
            <HideLoadingFallback />
            <ScrollToTop />
            <PageHeaderProvider>
            <Layout>
              <Suspense fallback={<LoadingFallback />}>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/design-system" element={<DesignSystem />} />
                  <Route path={createPageUrl('Home')} element={<Navigate to="/" replace />} />
                  <Route path={createPageUrl('Compendium')} element={<Compendium />} />
                  <Route path={createPageUrl('Rules')} element={<Rules />} />
                  <Route path={createPageUrl('CharacterCreator')} element={<CharacterCreator />} />
                  <Route path={createPageUrl('CharacterCreatorRandomize')} element={<CharacterCreatorRandomize />} />
                  <Route path={createPageUrl('CharacterSheet')} element={<CharacterSheet />} />
                  <Route path={createPageUrl('LevelUp')} element={<LevelUp />} />
                  <Route path={createPageUrl('MyCharacters')} element={<MyCharacters />} />
                  <Route path={createPageUrl('CombatTracker')} element={<CombatTracker />} />
                  <Route path={createPageUrl('EncounterBuilder')} element={<EncounterBuilder />} />
                  <Route path={createPageUrl('DMScreen')} element={<DMScreen />} />
                  <Route path={createPageUrl('Campaigns')} element={<Campaigns />} />
                  <Route path={createPageUrl('CampaignDetail')} element={<CampaignDetail />} />
                  <Route path="/campaigns/:campaignId/join" element={<CampaignJoin />} />
                  <Route path={createPageUrl('Wiki')} element={<Wiki />} />
                  <Route path={createPageUrl('Generators')} element={<Generators />} />
                  <Route path={createPageUrl('Homebrew')} element={<Homebrew />} />
                  <Route path={createPageUrl('SpellBook')} element={<SpellBook />} />
                  <Route path={createPageUrl('NewPlayer')} element={<NewPlayer />} />
                  <Route path={createPageUrl('NewDM')} element={<NewDM />} />
                  <Route path={createPageUrl('Glossary')} element={<Glossary />} />
                  <Route path={createPageUrl('Macros')} element={<Macros />} />
                  <Route path={createPageUrl('More')} element={<More />} />
                        <Route path={createPageUrl('Legal')} element={<Legal />} />
                        <Route path={createPageUrl('Privacy')} element={<Privacy />} />
                        <Route path={createPageUrl('Terms')} element={<Terms />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/account" element={<Account />} />
                  <Route path={createPageUrl('BugReport')} element={<BugReport />} />
                  <Route path="/upgrade" element={<Upgrade />} />
                  <Route path="/upgrade/success" element={<Upgrade />} />
                  <Route path="/upgrade/complete" element={<Upgrade />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </Layout>
            </PageHeaderProvider>
            </BrowserRouter>
            <ToastClickToDismiss />
            <PushForegroundBridge />
            <Toaster
              theme="dark"
              position="bottom-center"
              visibleToasts={2}
              expand={false}
              closeButton
              gap={8}
              duration={2200}
              offset={{ bottom: '4.75rem' }}
              mobileOffset={{ bottom: '5.25rem' }}
              swipeDirections={['down', 'left', 'right']}
              toastOptions={{
                className: 'mythkit-toast',
                duration: 2200,
                classNames: {
                  toast: 'mythkit-toast',
                  title: 'mythkit-toast-title',
                  description: 'mythkit-toast-description',
                  closeButton: 'mythkit-toast-close',
                },
              }}
            />
          </ThemeInit>
          </AuthProvider>
        </QueryClientProvider>
      </RootErrorBoundary>
      </React.StrictMode>
    );
    mobileLoggerSafe.info('[Init] React render call completed');
  } catch (renderError) {
    mobileLoggerSafe.error('[Init] React render failed:', renderError);
    throw renderError;
  }
}

setupLoadingFallback();

const initTimeout = setTimeout(() => {
  mobileLoggerSafe.error('App initialization timeout - React may have failed to render');
  const fallback = document.getElementById('loading-fallback');
  const reactMounted = typeof window !== 'undefined' && window.__mythkitAppMounted === true;
  if (fallback && fallback.style.display !== 'none' && !reactMounted) {
    fallback.style.display = 'none';
    rootEl.innerHTML = `
      <div style="display: flex; align-items: center; justify-content: center; height: 100vh; padding: 20px; text-align: center; background: var(--parchment, #FFF8E7); color: var(--text-dark, #2C1810);">
        <div>
          <h1 style="margin-bottom: 16px; color: var(--forest-green, #2D5016);">Initialization Timeout</h1>
          <p style="margin-bottom: 16px;">The app is taking longer than expected to load. This may indicate a network or JavaScript error.</p>
          <p style="margin-bottom: 16px; font-size: 0.9em; color: var(--text-muted, #6B5D54);">Try reloading or check your network connection.</p>
          <button onclick="window.location.reload()" style="padding: 12px 24px; background: var(--forest-green, #2D5016); color: white; border: none; border-radius: 8px; cursor: pointer; font-weight: bold;">
            Reload App
          </button>
        </div>
      </div>
    `;
  }
}, 8000);

initApp()
  .then(() => {
    clearTimeout(initTimeout);
    if (loadingFallbackTimeoutId !== null) {
      clearTimeout(loadingFallbackTimeoutId);
      loadingFallbackTimeoutId = null;
    }
    if (typeof window !== 'undefined' && window.__htmlLoadingTimeoutId) {
      clearTimeout(window.__htmlLoadingTimeoutId);
      window.__htmlLoadingTimeoutId = null;
    }
    mobileLoggerSafe.info('[Init] App initialized successfully');
  })
  .catch((error) => {
    clearTimeout(initTimeout);
    if (loadingFallbackTimeoutId !== null) {
      clearTimeout(loadingFallbackTimeoutId);
      loadingFallbackTimeoutId = null;
    }
    mobileLoggerSafe.error('Failed to initialize app:', error);
    const fallback = document.getElementById('loading-fallback');
    if (fallback) {
      fallback.style.display = 'none';
    }
    rootEl.innerHTML = `
      <div style="display: flex; align-items: center; justify-content: center; height: 100vh; padding: 20px; text-align: center; background: var(--parchment, #FFF8E7); color: var(--text-dark, #2C1810);">
        <div>
          <h1 style="margin-bottom: 16px; color: var(--forest-green, #2D5016);">Failed to Load App</h1>
          <p style="margin-bottom: 16px;">${error.message || 'Unknown error occurred'}</p>
          <p style="margin-bottom: 16px; font-size: 0.9em; color: var(--text-muted, #6B5D54);">Try reloading the app.</p>
          <button onclick="window.location.reload()" style="padding: 12px 24px; background: var(--forest-green, #2D5016); color: white; border: none; border-radius: 8px; cursor: pointer; font-weight: bold;">
            Reload App
          </button>
        </div>
      </div>
    `;
  });
